import { useState } from "react";
import { CheckIcon } from "@heroicons/react/24/outline";
import { CTA_GHOST, LINK_MUTED } from "@/onboarding/ui/theme";
import RailShell from "@/onboarding/ui/RailShell";
import { useLocation, useNavigate } from "react-router-dom";
import {
  APPLY_FROM_BUILDER,
  BUILD_ON_BUILDER,
  COMPLETION,
  DEMO_STEPS,
} from "./flows";
import { useDemoGuideStore } from "./useDemoGuideStore";
import { visibleSteps } from "./visibleSteps";
import FlowCompleteCta from "./FlowCompleteCta";
import { PricingModal } from "@/ui-components/PricingModal";
import { getTrialCtaLabel } from "@/utils/currency";
import { trackTrialCtaClick } from "@/utils/gtm";

/**
 * The instruction panel for the guided demo run.
 *
 * Chrome, sizing and z-order live in RailShell (src/onboarding/ui/), shared
 * with the trialist onboarding rail — this file owns only what differs: the
 * copy, the corner, and what the buttons do.
 *
 * It never anchors to an element. The dashboard drags, resizes and reflows
 * across five breakpoints, so a measured position would be stale the moment
 * anything moved. The panel it refers to is ringed instead, by grid id.
 */
/** Already on the page a step wants to send them to. */
function alreadyThere(to: string | undefined, pathname: string): boolean {
  if (!to) return false;
  if (to === "/dashboard") {
    return pathname === "/" || pathname.startsWith("/dashboard");
  }
  return pathname.startsWith(to);
}

export default function GuideRail() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { state, exitRun, advanceStep } = useDemoGuideStore((s) => ({
    state: s.state,
    exitRun: s.exitRun,
    advanceStep: s.advanceStep,
  }));

  // Declared before the early return below — a hook after it would be
  // conditional. FlowCompleteCta owns its own modal for the end-of-run ask;
  // the two never coexist, since the footer only renders while !done.
  const [pricingOpen, setPricingOpen] = useState(false);

  if (!state?.assetClass) return null;

  const templateId = state.templateId;

  const steps = visibleSteps(state.assetClass);
  const done = !!state.completedAt;
  const step = steps[state.step];

  if (!done && !step) return null;

  // The apply step reads differently depending on where they are standing:
  // "apply it to the feed" is not an instruction you can follow on a page with
  // no feed on it.
  const offDashboard =
    step?.id === "highlight" &&
    !(pathname === "/" || pathname.startsWith("/dashboard"));
  // Standing on the builder already: telling them to open it is noise.
  const onBuilder =
    step?.id === "watchlist" && pathname.startsWith("/watchlists-filters");

  const instruction = offDashboard
    ? APPLY_FROM_BUILDER.instruction
    : onBuilder
      ? BUILD_ON_BUILDER.instruction
      : step?.instruction;
  const hint = offDashboard
    ? APPLY_FROM_BUILDER.hint
    : onBuilder
      ? BUILD_ON_BUILDER.hint
      : step?.hint;

  /**
   * The button the rail offers, and what pressing it does.
   *
   * A step with somewhere to be gets taken there — pressing "Open Watchlists"
   * is the same as clicking Watchlists in the sidebar, which is what the
   * instruction was asking for. It does NOT advance the run: arriving on the
   * page is not the same as doing the thing on it, and the step still ends on
   * its own trigger (creating the watchlist, applying it).
   *
   * Steps with nowhere to go and no detectable action keep a plain Next.
   */
  const action = offDashboard
    ? APPLY_FROM_BUILDER.action
    : onBuilder
      ? // Already on the builder: the rail offers nothing. The two presses it
        // is asking for belong to the page, and a button here either does
        // them for the visitor or does nothing at all — it has been both.
        undefined
      : !alreadyThere(step?.action?.to, pathname)
        ? step?.action
        : undefined;

  /**
   * Which corner keeps out of the way of the thing being asked for.
   *
   * A fixed position cannot work for a guide that points at different parts of
   * different pages: top-right clears the dashboard's leftmost panels but
   * lands squarely on the builder's "Build it for me" card. So it moves — and
   * moving is the lesser evil, because a panel that shifts once between steps
   * is easier to follow than one that covers the button it is naming.
   */
  const onBuilderPage = pathname.startsWith("/watchlists-filters");
  const corner = onBuilderPage
    ? // The builder's two start cards sit top-left and top-centre.
      "bottom-6 right-5"
    : // The dashboard's primary and filtered wires are the leftmost panels.
      "right-5 top-[4.25rem]";

  return (
    <>
    <PricingModal isOpen={pricingOpen} onClose={() => setPricingOpen(false)} />
    <RailShell
      eyebrow="Guided demo"
      stepIndex={state.step}
      stepTotal={steps.length}
      showProgress={!done}
      corner={corner}
      onClose={exitRun}
      closeLabel="Close guide"
      closeAnalyticsId="demo-guide-close"
      footer={
        /* Always present. The primary button navigates rather than advancing,
           so without this a manual step could have no way forward at all —
           which is how someone gets stuck on the page the guide just sent them
           to. */
        !done && (
          <>
            {step!.trigger.kind !== "manual" || onBuilder ? (
              <span className="inline-flex items-center gap-1.5 text-sm text-gray-500">
                <CheckIcon className="h-3.5 w-3.5" />
                Continues when you do it
              </span>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-4">
              {/**
               * The way out for someone who already knows they want it.
               *
               * FlowCompleteCta is deliberately the only HARD ask in the run,
               * and that stands — this is a quiet link, not a second primary
               * button competing with the step. But it only appeared at the
               * end, so a returning visitor who has seen the demo before and
               * arrived ready to buy had to walk the whole run again to reach
               * a price. Muted styling keeps the instruction dominant; being
               * present at all is the point.
               */}
              <button
                type="button"
                onClick={() => {
                  trackTrialCtaClick({
                    surface: "demo_guide",
                    ...(templateId && { flow: templateId }),
                  });
                  setPricingOpen(true);
                }}
                className="cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-squawk-blue transition-colors hover:text-squawk-blue/80"
                data-analytics-id="demo-guide-rail-trial-cta"
              >
                {getTrialCtaLabel()}
              </button>
              <button
                type="button"
                onClick={() => advanceStep({ skipped: true })}
                className={LINK_MUTED}
                data-analytics-id={`demo-guide-skip-${step!.id}`}
              >
                Skip step
              </button>
            </div>
          </>
        )
      }
    >
      <div
        key={done ? "done" : step!.id}
        className="nq-onb-rise flex flex-1 flex-col"
      >
        {done ? (
          <FlowCompleteCta completion={COMPLETION} />
        ) : (
          <>
            <p className="text-2xl font-semibold leading-snug text-gray-100 short-screen:text-xl">
              {instruction}
            </p>
            {hint && (
              <p className="mt-3 text-base leading-relaxed text-gray-300 short-screen:mt-2 short-screen:text-sm">
                {hint}
              </p>
            )}
            {action ? (
              <button
                type="button"
                onClick={() => navigate(action.to)}
                className={`${CTA_GHOST} mt-auto w-full py-3.5 text-base`}
                // Navigation, not completion — this button does not fire
                // flow_step, because arriving on a page is not doing the
                // thing on it. The id is here so the distinction stays
                // legible in a click-selector report.
                data-analytics-id={`demo-guide-goto-${step!.id}`}
              >
                {action.label}
              </button>
            ) : (
              /* Not on the builder: a manual step with nowhere to go keeps a
                 plain Next. On the builder there is deliberately neither — a
                 Next here would be a one-press bypass of the only thing the
                 step is trying to get them to do, which is worse than the
                 no-op button it replaced. The footer says what it is waiting
                 for instead. */
              step!.trigger.kind === "manual" &&
              !onBuilder && (
                <button
                  type="button"
                  onClick={() => advanceStep()}
                  className={`${CTA_GHOST} mt-auto w-full py-3.5 text-base`}
                  data-analytics-id={`demo-guide-next-${step!.id}`}
                >
                  Next
                </button>
              )
            )}
          </>
        )}
      </div>
    </RailShell>
    </>
  );
}

export { DEMO_STEPS };
